import React from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Box,
  Typography,
  Grid,
  Paper,
  Chip,
  Divider,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import {
  LocalShipping as LocalShippingIcon,
  Business as BusinessIcon,
  Receipt as ReceiptIcon,
  ShoppingCart as ShoppingCartIcon,
  Link as LinkIcon
} from '@mui/icons-material';
import { formatCustomerName } from '../../utils/formatCustomerName';

const ViewDeliveryModal = ({ 
  open, 
  onClose, 
  delivery,
  onEdit,
  onLinkOrder 
}) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  if (!delivery) return null;

  const formatCurrency = (value) => {
    return new Intl.NumberFormat('it-IT', { 
      style: 'currency', 
      currency: 'EUR' 
    }).format(value || 0);
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return 'N/D';
    return new Date(dateStr).toLocaleDateString('it-IT');
  };

  const getTypeColor = (type) => {
    switch (type) {
      case 'normal': return 'primary';
      case 'emergency': return 'error';
      case 'top-up': return 'info';
      default: return 'default';
    }
  };

  const getTypeLabel = (type) => {
    switch (type) {
      case 'normal': return 'Normale';
      case 'emergency': return 'Urgente';
      case 'top-up': return 'Rabbocco';
      default: return type;
    }
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth fullScreen={isMobile}>
      <DialogTitle>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <LocalShippingIcon color="primary" />
            <span>Rifornimento {delivery.delivery_code || delivery.id}</span>
          </Box>
          <Chip 
            label={getTypeLabel(delivery.delivery_type)}
            color={getTypeColor(delivery.delivery_type)}
            size="small"
          />
        </Box>
      </DialogTitle>
      <DialogContent>
        <Grid container spacing={2}>
          {/* Dati Consegna */}
          <Grid item xs={12}>
            <Paper variant="outlined" sx={{ p: 2 }}>
              <Typography variant="subtitle2" color="primary" gutterBottom>
                Dati Rifornimento
              </Typography>
              <Grid container spacing={2}>
                <Grid item xs={3}>
                  <Typography variant="caption" color="text.secondary">Data Consegna</Typography>
                  <Typography fontWeight="bold">{formatDate(delivery.delivery_date)}</Typography>
                </Grid>
                <Grid item xs={3}>
                  <Typography variant="caption" color="text.secondary">DDT Numero</Typography>
                  <Typography fontWeight="bold">{delivery.ddt_number || 'N/D'}</Typography>
                </Grid>
                <Grid item xs={3}>
                  <Typography variant="caption" color="text.secondary">Data DDT</Typography>
                  <Typography>{formatDate(delivery.ddt_date)}</Typography>
                </Grid>
                <Grid item xs={3}>
                  <Typography variant="caption" color="text.secondary">Quantità</Typography>
                  <Typography fontWeight="bold" color="primary" variant="h6">
                    {parseFloat(delivery.quantity_liters || 0).toLocaleString('it-IT')} L
                  </Typography>
                </Grid>
              </Grid>
            </Paper>
          </Grid>

          {/* Impianto */}
          <Grid item xs={12} md={6}>
            <Paper variant="outlined" sx={{ p: 2, height: '100%' }}>
              <Typography variant="subtitle2" color="primary" gutterBottom>
                Impianto / Contratto
              </Typography>
              {delivery.plant ? (
                <>
                  <Typography fontWeight="bold">
                    Cod. Impianto: {delivery.plant.plant_code || 'N/D'}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Cod. Consegna: {delivery.plant.delivery_code || delivery.plant_code || 'N/D'}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    {delivery.plant.indirizzo || delivery.plant_address || ''}
                    {delivery.plant.citta ? `, ${delivery.plant.citta}` : ''}
                  </Typography>
                  {delivery.plant.customer && (
                    <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                      Cliente: {formatCustomerName(delivery.plant.customer)}
                    </Typography>
                  )}
                </>
              ) : delivery.plant_code || delivery.plant_address ? (
                <>
                  <Typography fontWeight="bold">
                    Cod. Consegna: {delivery.plant_code || 'N/D'}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    {delivery.plant_address || 'Indirizzo non disponibile'}
                  </Typography>
                  <Chip 
                    label="Impianto non trovato in anagrafica" 
                    size="small" 
                    color="warning" 
                    variant="outlined"
                    sx={{ mt: 1 }}
                  />
                </>
              ) : delivery.contract ? (
                <>
                  <Typography fontWeight="bold">
                    {delivery.contract.cod_con || delivery.contract.nome || `Contratto #${delivery.contract.id}`}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    {delivery.contract.nome || ''}
                  </Typography>
                  {delivery.contract.indirizzo && (
                    <Typography variant="body2" color="text.secondary">
                      {delivery.contract.indirizzo}
                    </Typography>
                  )}
                </>
              ) : (
                <Typography color="text.secondary">Nessun impianto associato</Typography>
              )}
            </Paper>
          </Grid>

          {/* Fornitore */}
          <Grid item xs={12} md={6}>
            <Paper variant="outlined" sx={{ p: 2, height: '100%' }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                <BusinessIcon color="primary" fontSize="small" />
                <Typography variant="subtitle2" color="primary">Fornitore</Typography>
              </Box>
              {delivery.supplier ? (
                <>
                  <Typography fontWeight="bold">
                    {delivery.supplier.company_name}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    P.IVA: {delivery.supplier.vat_number || 'N/D'}
                  </Typography>
                  {delivery.supplier.phone && (
                    <Typography variant="body2" color="text.secondary">
                      Tel: {delivery.supplier.phone}
                    </Typography>
                  )}
                </>
              ) : (
                <Typography color="text.secondary">Nessun fornitore associato</Typography>
              )}
            </Paper>
          </Grid>

          {/* Dettaglio Economico */}
          <Grid item xs={12}>
            <Paper variant="outlined" sx={{ p: 2 }}>
              <Typography variant="subtitle2" color="primary" gutterBottom>
                Dettaglio Economico
              </Typography>
              <Grid container spacing={3}>
                <Grid item xs={2}>
                  <Typography variant="body2" color="text.secondary">Prezzo/Litro</Typography>
                  <Typography variant="h6">{formatCurrency(delivery.price_per_liter)}/L</Typography>
                </Grid>
                <Grid item xs={2}>
                  <Typography variant="body2" color="text.secondary">Quantità</Typography>
                  <Typography variant="h6">
                    {parseFloat(delivery.quantity_liters || 0).toLocaleString('it-IT')} L
                  </Typography>
                </Grid>
                <Grid item xs={2}>
                  <Typography variant="body2" color="text.secondary">Imponibile</Typography>
                  <Typography variant="h6">{formatCurrency(delivery.taxable_amount)}</Typography>
                </Grid>
                <Grid item xs={2}>
                  <Typography variant="body2" color="text.secondary">IVA ({delivery.vat_rate || 22}%)</Typography>
                  <Typography variant="h6">{formatCurrency(delivery.vat_amount)}</Typography>
                </Grid>
                <Grid item xs={2}>
                  <Typography variant="body2" color="text.secondary">Totale</Typography>
                  <Typography variant="h6" color="primary" fontWeight="bold">
                    {formatCurrency(delivery.total_amount)}
                  </Typography>
                </Grid>
                <Grid item xs={2}>
                  <Typography variant="body2" color="text.secondary">Tipo</Typography>
                  <Chip 
                    label={getTypeLabel(delivery.delivery_type)}
                    color={getTypeColor(delivery.delivery_type)}
                    size="small"
                  />
                </Grid>
              </Grid>
            </Paper>
          </Grid>

          {/* Ordine Associato */}
          {delivery.order && (
            <Grid item xs={12} md={6}>
              <Paper variant="outlined" sx={{ p: 2, bgcolor: 'info.50' }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                  <ShoppingCartIcon color="info" fontSize="small" />
                  <Typography variant="subtitle2" color="info.dark">
                    Ordine Associato
                  </Typography>
                </Box>
                <Grid container spacing={2}>
                  <Grid item xs={6}>
                    <Typography variant="body2" color="text.secondary">Codice Ordine</Typography>
                    <Typography fontWeight="bold">
                      {delivery.order.order_code || `#${delivery.order.id}`}
                    </Typography>
                  </Grid>
                  <Grid item xs={6}>
                    <Typography variant="body2" color="text.secondary">Data Ordine</Typography>
                    <Typography>{formatDate(delivery.order.order_date)}</Typography>
                  </Grid>
                </Grid>
              </Paper>
            </Grid>
          )}

          {/* Fattura Associata */}
          {delivery.supplierInvoice && (
            <Grid item xs={12} md={delivery.order ? 6 : 12}>
              <Paper variant="outlined" sx={{ p: 2, bgcolor: 'success.50' }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                  <ReceiptIcon color="success" fontSize="small" />
                  <Typography variant="subtitle2" color="success.dark">
                    Fattura Fornitore Associata
                  </Typography>
                </Box>
                <Grid container spacing={2}>
                  <Grid item xs={6}>
                    <Typography variant="body2" color="text.secondary">Numero Fattura</Typography>
                    <Typography fontWeight="bold">
                      {delivery.supplierInvoice.invoice_number}
                    </Typography>
                  </Grid>
                  <Grid item xs={6}>
                    <Typography variant="body2" color="text.secondary">Data Fattura</Typography>
                    <Typography>{formatDate(delivery.supplierInvoice.invoice_date)}</Typography>
                  </Grid>
                </Grid>
              </Paper>
            </Grid>
          )}

          {/* Collegamento mancante */}
          {!delivery.order_id && (
            <Grid item xs={12}>
              <Paper variant="outlined" sx={{ p: 2, bgcolor: 'warning.50' }}>
                <Typography color="warning.dark" fontWeight="bold" gutterBottom>
                  ⚠️ Questo rifornimento non è collegato a nessun ordine
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Puoi collegarlo ad un ordine esistente dalla scheda Riconciliazione.
                </Typography>
              </Paper>
            </Grid>
          )}

          {/* Note */}
          {delivery.notes && (
            <Grid item xs={12}>
              <Paper variant="outlined" sx={{ p: 2 }}>
                <Typography variant="subtitle2" color="primary" gutterBottom>Note</Typography>
                <Typography variant="body2">{delivery.notes}</Typography>
              </Paper>
            </Grid>
          )}

          {/* Metadati */}
          <Grid item xs={12}>
            <Divider sx={{ my: 1 }} />
            <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
              <Typography variant="caption" color="text.secondary">
                Creato il: {formatDate(delivery.createdAt)}
              </Typography>
              <Typography variant="caption" color="text.secondary">
                Ultimo aggiornamento: {formatDate(delivery.updatedAt)}
              </Typography>
            </Box>
          </Grid>
        </Grid>
      </DialogContent>
      <DialogActions>
        {!delivery.order_id && onLinkOrder && (
          <Button 
            onClick={() => onLinkOrder(delivery)} 
            startIcon={<LinkIcon />}
          >
            Collega a Ordine
          </Button>
        )}
        {onEdit && (
          <Button onClick={() => onEdit(delivery)} color="primary">
            Modifica
          </Button>
        )}
        <Button onClick={onClose} variant="contained">Chiudi</Button>
      </DialogActions>
    </Dialog>
  );
};

export default ViewDeliveryModal;
